import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  Bell,
  BookOpenCheck,
  Building2,
  Check,
  CircleDollarSign,
  FileCheck2,
  GitBranch,
  Landmark,
  LineChart,
  LockKeyhole,
  Network,
  ReceiptText,
  ShieldCheck,
  Smartphone,
  UsersRound,
  WalletCards,
  Workflow,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { LeadForm } from '../components/LeadForm';
import { MemberPhonePreview, ProductPreview } from '../components/ProductPreview';
import { Seo } from '../components/Seo';
import { config } from '../lib/config';

const platformModules = [
  { icon: UsersRound, title: 'Member lifecycle', text: 'Onboard, verify, manage and support member records.' },
  { icon: CircleDollarSign, title: 'Contributions & savings', text: 'Configure products, commitments, changes and balances.' },
  { icon: Landmark, title: 'Loans & guarantors', text: 'Manage applications, eligibility, guarantors and schedules.' },
  { icon: LineChart, title: 'Investments', text: 'Configure products, placements, returns and maturity activity.' },
  { icon: Banknote, title: 'Payments & collections', text: 'Payment accounts, confirmations, controls and reconciliation.' },
  { icon: BookOpenCheck, title: 'Accounting & reports', text: 'General-ledger operations, statements and management reports.' },
  { icon: Workflow, title: 'Approvals & governance', text: 'Role-aware queues, decision evidence and step-up controls.' },
  { icon: GitBranch, title: 'Platform administration', text: 'Plans, entitlements, tenants, roles and feature control.' },
];

const cooperativeTypes = [
  { icon: Building2, title: 'Primary cooperatives', text: 'Run day-to-day member services and financial operations from one controlled system.' },
  { icon: Network, title: 'Secondary unions', text: 'Coordinate societies, oversight structures and shared operational standards.' },
  { icon: UsersRound, title: 'Supporting organisations', text: 'Provide authorised services without weakening tenant ownership or access control.' },
  { icon: LineChart, title: 'Enterprise operations', text: 'Support larger multi-role teams with configurable permissions and feature entitlements.' },
];

const memberFeatures = [
  { icon: WalletCards, title: 'Check balances', text: 'View contributions, loans, investments and account activity.' },
  { icon: CircleDollarSign, title: 'Contribute & save', text: 'Make payments and submit contribution change requests.' },
  { icon: Landmark, title: 'Apply & manage loans', text: 'Submit applications, provide guarantor details and track status.' },
  { icon: Banknote, title: 'Make payments', text: 'Use payment accounts and confirm eligible transactions securely.' },
  { icon: Bell, title: 'Get notifications', text: 'Receive important operational and financial updates.' },
  { icon: ReceiptText, title: 'Requests & statements', text: 'Submit service requests and access member statements.' },
];

export default function HomePage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'CoopCore',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Android, iOS',
    description: 'Cooperative banking, member self-service and controlled operations platform.',
    url: config.marketingUrl,
  };

  return (
    <div className="marketing-home">
      <Seo
        title="CoopCore | Cooperative Banking and Operations Platform"
        description="Run member services, contributions, loans, investments, payments, approvals, accounting and reporting on one secure cooperative platform."
        path="/"
        schema={schema}
      />

      <section className="home-hero">
        <div className="home-hero-pattern" aria-hidden="true" />
        <div className="container home-hero-grid">
          <div className="home-hero-copy">
            <div className="home-eyebrow"><span /> The finance and operations platform for cooperatives</div>
            <h1><span>Modern cooperative</span><span>banking. Unified</span><span>operations. <em>Real</em></span><span><em>member impact</em></span></h1>
            <p>CoopCore unifies member services, contributions, loans, investments, payments, accounting and governance so cooperatives can operate with confidence.</p>
            <div className="home-hero-actions">
              <Link className="home-button home-button-accent" to="/contact">Request a demo <ArrowRight /></Link>
              <Link className="home-button home-button-outline" to="/product">Explore the platform <span className="play-dot">▶</span></Link>
            </div>
          </div>
          <ProductPreview />
        </div>
        <div className="container assurance-ribbon">
          <article><span><ShieldCheck /></span><div><strong>Role-based access</strong><small>Server-issued permissions and tenant-aware controls.</small></div></article>
          <article><span><FileCheck2 /></span><div><strong>Audit-ready workflows</strong><small>Decision evidence, correlation IDs and financial records.</small></div></article>
          <article><span><UsersRound /></span><div><strong>Member self-service</strong><small>Mobile-first access to everyday cooperative services.</small></div></article>
          <article><span><LockKeyhole /></span><div><strong>Secure payments</strong><small>Controlled payment accounts, confirmations and navigation.</small></div></article>
        </div>
      </section>

      <section className="outcomes-section">
        <div className="container outcomes-grid">
          <div className="outcomes-copy">
            <span className="home-kicker">Built for outcomes</span>
            <h2>Technology that strengthens cooperatives and the people they serve.</h2>
            <p>CoopCore brings member convenience and operational control into one platform, helping teams reduce fragmented work and deliver a clearer member experience.</p>
            <ul className="outcome-points">
              <li><span><ShieldCheck /></span><div><strong>Stronger member trust</strong><small>Transparent records, controlled access and visible request status.</small></div></li>
              <li><span><UsersRound /></span><div><strong>Sustainable growth</strong><small>Configurable products and channels that can evolve with the cooperative.</small></div></li>
              <li><span><Workflow /></span><div><strong>Operational excellence</strong><small>Connected workflows that reduce duplicate handling and manual hand-offs.</small></div></li>
            </ul>
          </div>
          <div className="outcomes-collage" aria-label="Cooperative teams using CoopCore">
            <img className="team-photo" src="/site-assets/cooperative-team.webp" alt="A cooperative team reviewing information together" />
            <article className="growth-card"><small>Member growth</small><strong>Track trends</strong><span>See member activity and segment movement.</span><div className="mini-bars">{[36,48,46,60,68,76,88].map((h,i)=><i key={i} style={{height:`${h}%`}} />)}</div></article>
            <article className="efficiency-card"><small>Cost to serve</small><strong>Measure efficiency</strong><span>Understand operational workload across services.</span><svg viewBox="0 0 280 120" aria-hidden="true"><path className="eff-area" d="M0 30L35 44L70 28L105 50L140 45L175 65L210 72L245 95L280 105L280 120L0 120Z"/><path className="eff-line" d="M0 30L35 44L70 28L105 50L140 45L175 65L210 72L245 95L280 105"/></svg></article>
            <img className="member-photo" src="/site-assets/member-mobile.webp" alt="A cooperative member using the mobile app" />
          </div>
        </div>
      </section>

      <section className="platform-section">
        <div className="container platform-panel" id="platform">
          <span className="home-kicker home-kicker-light">The CoopCore platform</span>
          <h2>Everything you need. One unified platform.</h2>
          <div className="platform-module-grid">
            {platformModules.map(({ icon: Icon, title, text }) => (
              <article key={title}><span><Icon /></span><div><strong>{title}</strong><p>{text}</p></div></article>
            ))}
          </div>
        </div>
      </section>

      <section className="cooperative-section" id="solutions">
        <div className="container">
          <div className="cooperative-heading"><span className="home-kicker">Built for every cooperative</span><h2>One platform. Every cooperative.</h2></div>
          <div className="cooperative-grid">
            {cooperativeTypes.map(({ icon: Icon, title, text }, index) => {
              const paths = ['/solutions#primary', '/solutions#unions', '/solutions#partners', '/solutions#enterprise'];
              return <article key={title}><Icon /><h3>{title}</h3><p>{text}</p><Link to={paths[index]}>Learn more <ArrowRight /></Link></article>;
            })}
          </div>

          <div className="trust-grid">
            <img src="/site-assets/cooperative-leader.webp" alt="A cooperative leader using CoopCore" />
            <div className="trust-copy">
              <span className="home-kicker">Platform trust</span>
              <h2>Security, transparency and accountability at the core.</h2>
              <ul>
                <li><span><LockKeyhole /></span><div><strong>Permission-controlled access</strong><small>Roles, tenant context, feature entitlements and ownership checks work together.</small></div></li>
                <li><span><FileCheck2 /></span><div><strong>Auditable decisions</strong><small>Approval actions, correlation IDs and financial records preserve operational evidence.</small></div></li>
                <li><span><ReceiptText /></span><div><strong>Your data, your control</strong><small>Tenant-isolated records, export workflows and controlled administration.</small></div></li>
              </ul>
            </div>
            <aside className="growth-panel">
              <h3>Designed to grow with you</h3>
              <ul><li><Check /> Start with the modules you need</li><li><Check /> Enable features through plan entitlements</li><li><Check /> Connect approved integrations and APIs</li><li><Check /> Add roles without weakening access controls</li></ul>
              <div>CoopCore grows with your cooperative, while keeping governance and member service connected.</div>
            </aside>
          </div>
        </div>
      </section>

      <section className="member-experience-section" id="member-experience">
        <div className="container member-experience-panel">
          <div className="member-phone-wrap"><MemberPhonePreview /></div>
          <div className="member-experience-copy">
            <span className="home-kicker">Member-first experience</span>
            <h2>Everything members need, right at their fingertips.</h2>
            <div className="member-feature-grid">
              {memberFeatures.map(({ icon: Icon, title, text }) => (
                <article key={title}><span><Icon /></span><div><strong>{title}</strong><p>{text}</p></div></article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="home-security-section">
        <div className="container home-security-grid">
          <div className="home-security-copy">
            <span className="home-kicker home-kicker-light">Security & governance</span>
            <h2>Enterprise-grade security. Built for trust.</h2>
            <p>CoopCore protects sensitive cooperative workflows with tenant isolation, role-aware access, MFA, session controls, audit evidence and protected payment handling.</p>
            <Link className="home-button home-button-accent" to="/security">Explore security <ArrowRight /></Link>
          </div>
          <div className="home-security-controls">
            <article><span><LockKeyhole /></span><div><strong>Identity and sessions</strong><small>MFA, token rotation, session expiry, device registration and revocation.</small></div></article>
            <article><span><UsersRound /></span><div><strong>Role-based access</strong><small>Backend-enforced permissions, tenant features and ownership controls.</small></div></article>
            <article><span><FileCheck2 /></span><div><strong>Audit trails</strong><small>Decision records, correlation IDs and workflow evidence across sensitive actions.</small></div></article>
            <article><span><BadgeCheck /></span><div><strong>Governed workflows</strong><small>Approval steps, maker-checker separation and step-up verification.</small></div></article>
          </div>
          <img className="security-lock-art" src="/site-assets/security-lock.webp" alt="Illustration representing CoopCore security controls" />
        </div>
      </section>

      <section className="home-contact-section">
        <div className="container home-contact-grid">
          <div className="home-contact-copy">
            <span className="home-kicker">Ready to get started?</span>
            <h2>See CoopCore in action.</h2>
            <p>Request a focused walkthrough and see how CoopCore can support your cooperative’s member services, products, approvals and operational model.</p>
            <div className="contact-benefits"><span><ShieldCheck /> Tailored walkthrough</span><span><BadgeCheck /> No commitment</span><span><UsersRound /> Product specialist</span></div>
          </div>
          <LeadForm compact />
        </div>
      </section>
    </div>
  );
}
